import React, { useState } from 'react';
import { X, Search } from 'lucide-react';
import { useTheme } from '../../../core/ui/ThemeContext';

const EmojiPicker = ({ currentEmoji, onSelect, onClose }) => {
  const { isDarkMode } = useTheme();
  const [searchTerm, setSearchTerm] = useState('');

  // Kategorisierte Emoji-Auswahl für Shops
  const emojiCategories = {
    'Shops & Geschäfte': {
      emojis: ['🏪', '🏬', '🏢', '🏭', '🏦', '🏛️', '🏨', '🏫', '🏤', '🏣'],
      keywords: ['shop', 'store', 'building', 'office', 'bank', 'hotel', 'school', 'factory']
    },
    'Handel & Verkauf': {
      emojis: ['🛍️', '🛒', '💳', '💰', '💎', '🏷️', '📦', '📋', '🧾', '💸'],
      keywords: ['shopping', 'cart', 'money', 'diamond', 'tag', 'package', 'receipt', 'payment']
    },
    'Büro & Material': {
      emojis: ['📝', '📊', '📈', '📉', '📁', '📂', '🗂️', '📄', '📃', '🖇️', '📎', '✂️', '📐', '📏'],
      keywords: ['office', 'document', 'chart', 'folder', 'file', 'clip', 'scissors', 'ruler']
    },
    'IT & Technik': {
      emojis: ['💻', '🖥️', '📱', '⌚', '🖨️', '⌨️', '🖱️', '💾', '💿', '📀', '🔌', '🔋', '📷', '📹'],
      keywords: ['computer', 'laptop', 'phone', 'watch', 'printer', 'keyboard', 'mouse', 'disk', 'battery', 'camera']
    },
    'Fahrzeuge & Transport': {
      emojis: ['🚗', '🚙', '🚐', '🚛', '🚚', '🚜', '🏍️', '🚲', '🛴', '✈️', '🚁', '🚢', '⛵', '🚂'],
      keywords: ['car', 'truck', 'van', 'motorcycle', 'bike', 'plane', 'helicopter', 'ship', 'train']
    },
    'Werkzeuge & Industrie': {
      emojis: ['🔧', '🔨', '⚒️', '🛠️', '⚙️', '🔩', '⚡', '🔥', '💡', '🏗️', '⛏️', '🪓', '🔪', '📡'],
      keywords: ['tool', 'hammer', 'wrench', 'gear', 'bolt', 'electricity', 'fire', 'light', 'construction', 'antenna']
    },
    'Medizin & Gesundheit': {
      emojis: ['🏥', '⚕️', '💊', '🩺', '💉', '🦷', '👁️', '🧬', '🔬', '🧪', '🩹', '🚑', '♿', '🧴'],
      keywords: ['hospital', 'medical', 'pill', 'stethoscope', 'injection', 'tooth', 'eye', 'dna', 'microscope', 'ambulance']
    },
    'Essen & Trinken': {
      emojis: ['🍕', '🍔', '🌭', '🥪', '🌮', '🍜', '🍝', '🍳', '🥘', '🍲', '🥗', '🍰', '☕', '🍺'],
      keywords: ['food', 'pizza', 'burger', 'sandwich', 'soup', 'pasta', 'cooking', 'salad', 'cake', 'coffee', 'beer']
    },
    'Sport & Freizeit': {
      emojis: ['⚽', '🏀', '🏈', '🎾', '🏐', '🏓', '🎳', '🏸', '🥊', '🎮', '🎯', '🎲', '🃏', '🎪'],
      keywords: ['sport', 'ball', 'football', 'basketball', 'tennis', 'bowling', 'boxing', 'gaming', 'target', 'cards', 'circus']
    },
    'Natur & Umwelt': {
      emojis: ['🌱', '🌿', '🍃', '🌳', '🌲', '🌺', '🌻', '🌹', '🏔️', '🌊', '🔥', '❄️', '☀️', '⭐'],
      keywords: ['nature', 'plant', 'tree', 'flower', 'mountain', 'water', 'fire', 'snow', 'sun', 'star']
    }
  };

  // Alle Emojis für die Suche sammeln
  const allEmojis = Object.values(emojiCategories).flatMap(category =>
    category.emojis.map(emoji => ({
      emoji,
      keywords: category.keywords
    }))
  );

  // Gefilterte Emojis basierend auf Suchbegriff
  const getFilteredEmojis = () => {
    if (!searchTerm) return emojiCategories;

    const filtered = allEmojis.filter(item =>
      item.keywords.some(keyword =>
        keyword.toLowerCase().includes(searchTerm.toLowerCase())
      )
    );

    return {
      'Suchergebnisse': {
        emojis: filtered.map(item => item.emoji),
        keywords: []
      }
    };
  };

  const filteredCategories = getFilteredEmojis();

  return (
    <div className={`fixed inset-0 ${isDarkMode ? 'bg-black/60 backdrop-blur-sm' : 'bg-gray-900/40'} flex items-center justify-center p-4 z-50`}>
      <div className="relative group w-full max-w-2xl max-h-[80vh] overflow-hidden">
        {/* Modal Background */}
        <div className={`absolute inset-0 ${isDarkMode
          ? 'bg-gradient-to-br from-gray-900/40 via-gray-800/30 to-gray-900/40 border border-white/20 backdrop-blur-3xl shadow-[0_8px_32px_rgba(0,0,0,0.4)]'
          : 'bg-white border border-slate-200 shadow-lg'
        } rounded-3xl`}></div>
        <div className={`absolute inset-0 ${isDarkMode
          ? 'bg-gradient-to-t from-white/5 via-transparent to-white/10'
          : ''
        } rounded-3xl`}></div>

        <div className="relative">
          {/* Header */}
          <div className="flex items-center justify-between p-6">
            <div className="flex items-center space-x-3">
              {/* Icon */}
              <div className="relative group/icon">
                <div className={`absolute inset-0 ${isDarkMode ? 'bg-gradient-to-br from-white/20 via-white/10 to-transparent' : 'hidden'} rounded-2xl blur-sm`}></div>
                <div className={`relative ${isDarkMode
                  ? 'bg-gradient-to-br from-blue-600/60 via-blue-700/50 to-blue-800/60 backdrop-blur-xl shadow-[0_8px_32px_rgba(59,130,246,0.3),inset_0_1px_0_rgba(255,255,255,0.3)] border border-white/20'
                  : 'bg-brand/10'
                } w-10 h-10 rounded-2xl flex items-center justify-center`}>
                  <span className="text-xl relative z-10">😊</span>
                  <div className={`absolute top-1 left-1 w-2 h-2 bg-white/30 rounded-full blur-sm ${isDarkMode ? '' : 'hidden'}`}></div>
                </div>
              </div>
              <div>
                <h3 className={`text-lg font-bold ${isDarkMode ? 'bg-gradient-to-r from-white via-blue-200 to-white bg-clip-text text-transparent' : 'text-slate-900'}`}>Shop-Icon auswählen</h3>
                <p className={`text-sm ${isDarkMode ? 'text-white/70' : 'text-slate-600'}`}>Wählen Sie ein Emoji für Ihren internen Shop</p>
              </div>
            </div>

            {/* Close Button */}
            <button
              onClick={onClose}
              className={`relative group/close p-2 rounded-xl transition-all duration-200 transform hover:scale-110 ${
                isDarkMode ? '' : 'hover:bg-red-50 hover:text-red-600'
              }`}
              title="Schließen"
            >
              <div className={`absolute inset-0 ${isDarkMode
                ? 'bg-gray-900/20 border border-white/20 backdrop-blur-xl'
                : ''
              } rounded-xl opacity-0 group-hover/close:opacity-100 transition-opacity duration-200`}></div>
              <div className={`absolute inset-0 bg-red-500/10 rounded-xl opacity-0 group-hover/close:opacity-100 transition-opacity duration-200 ${isDarkMode ? '' : 'hidden'}`}></div>
              <X className={`relative w-5 h-5 ${isDarkMode ? 'text-white/70 group-hover/close:text-red-300' : 'text-slate-500 group-hover/close:text-red-600'} transition-colors duration-200`} />
            </button>
          </div>

          {/* Search */}
          <div className="p-6">
            <div className="relative group/search">
              <div className="relative">
                {/* Input Background */}
                <div className={`absolute inset-0 ${isDarkMode
                  ? 'bg-gray-800/30 border border-white/30 backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.2)]'
                  : 'bg-white border border-slate-200'
                } rounded-2xl`}></div>

                {/* Icon Container */}
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none z-10">
                  <Search className={`w-5 h-5 ${isDarkMode
                    ? 'text-white/60 group-focus-within/search:text-blue-300/80'
                    : 'text-slate-400 group-focus-within/search:text-brand'
                  } transition-colors duration-300`} />
                </div>

                <input
                  type="text"
                  placeholder="Emoji suchen (z.B. shop, office, tool)..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`relative w-full pl-12 pr-4 py-4 bg-transparent border-0 rounded-2xl focus:ring-2 focus:outline-none transition-all duration-300 ${
                    isDarkMode
                      ? 'focus:ring-blue-400/50 text-white placeholder-white/60'
                      : 'focus:ring-brand/40 text-slate-900 placeholder-slate-400'
                  }`}
                />

                {/* Focus Highlight */}
                <div className={`absolute inset-0 rounded-2xl border border-transparent ${
                  isDarkMode ? 'group-focus-within/search:border-blue-400/50' : 'group-focus-within/search:border-brand/40'
                } transition-all duration-300 pointer-events-none`}></div>
              </div>
            </div>
          </div>

          {/* Current Selection */}
          <div className="px-6 pb-6">
            <div className="relative group/current">
              <div className={`absolute inset-0 ${isDarkMode
                ? 'bg-gradient-to-br from-gray-900/20 via-gray-800/15 to-gray-900/20 border border-white/20 backdrop-blur-xl shadow-[0_4px_16px_rgba(0,0,0,0.2)]'
                : 'bg-slate-50 border border-slate-200'
              } rounded-2xl`}></div>
              <div className={`absolute inset-0 ${isDarkMode
                ? 'bg-gradient-to-t from-white/5 via-transparent to-white/10'
                : ''
              } rounded-2xl`}></div>

              <div className="relative p-4 flex items-center space-x-4">
                <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-slate-700'}`}>Aktuell ausgewählt:</span>
                <div className="relative group/selected">
                  <div className={`absolute inset-0 ${isDarkMode ? 'bg-gradient-to-br from-white/20 via-white/10 to-transparent' : 'hidden'} rounded-2xl blur-sm`}></div>
                  <div className={`relative ${isDarkMode
                    ? 'bg-gradient-to-br from-blue-600/60 via-blue-700/50 to-blue-800/60 backdrop-blur-xl shadow-[0_8px_32px_rgba(59,130,246,0.3),inset_0_1px_0_rgba(255,255,255,0.3)] border border-white/20'
                    : 'bg-brand/10 border border-brand/20'
                  } w-12 h-12 rounded-2xl flex items-center justify-center`}>
                    <span className="text-2xl relative z-10">{currentEmoji}</span>
                    <div className={`absolute top-1 left-1 w-3 h-3 bg-white/30 rounded-full blur-sm ${isDarkMode ? '' : 'hidden'}`}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Emoji Categories */}
          <div className="flex-1 overflow-y-auto px-6 pb-6 max-h-96">
            {Object.entries(filteredCategories).map(([categoryName, category]) => (
              <div key={categoryName} className="mb-6">
                {/* Category Header */}
                <div className="relative group/categoryheader mb-4">
                  <div className={`absolute inset-0 ${isDarkMode
                    ? 'bg-gradient-to-br from-gray-900/20 via-gray-800/15 to-gray-900/20 border border-white/20 backdrop-blur-xl shadow-[0_2px_8px_rgba(0,0,0,0.1)]'
                    : 'bg-slate-50 border border-slate-200'
                  } rounded-2xl`}></div>
                  <div className={`absolute inset-0 ${isDarkMode
                    ? 'bg-gradient-to-t from-white/5 via-transparent to-white/10'
                    : ''
                  } rounded-2xl`}></div>

                  <div className="relative px-4 py-2">
                    <h4 className={`text-sm font-semibold ${isDarkMode ? 'text-white/80' : 'text-slate-600'} uppercase tracking-wide`}>
                      {categoryName}
                    </h4>
                  </div>
                </div>

                {/* Emoji Grid */}
                <div className="grid grid-cols-8 sm:grid-cols-10 gap-3">
                  {category.emojis.map((emoji, index) => (
                    <button
                      key={`${categoryName}-${index}`}
                      onClick={() => onSelect(emoji)}
                      className="relative group/emoji w-10 h-10 transition-all duration-200 transform hover:scale-110"
                      title={`${emoji} auswählen`}
                    >
                      {/* Button Background */}
                      <div className={`absolute inset-0 rounded-xl border transition-all duration-200 ${
                        currentEmoji === emoji
                          ? (isDarkMode
                            ? 'bg-blue-500/30 border-blue-400/50 shadow-[0_4px_16px_rgba(59,130,246,0.4)] backdrop-blur-xl'
                            : 'bg-brand/10 border-brand/40')
                          : (isDarkMode
                            ? 'bg-gray-800/20 border-white/20 hover:bg-blue-500/20 hover:border-blue-400/40 backdrop-blur-xl group-hover/emoji:shadow-[0_4px_16px_rgba(59,130,246,0.2)]'
                            : 'bg-white border-slate-200 hover:bg-brand/5 hover:border-brand/20')
                      }`}></div>

                      {/* Highlight Effect (nur Dark) */}
                      <div className={`absolute inset-0 bg-gradient-to-t transition-all duration-200 rounded-xl ${isDarkMode
                        ? (currentEmoji === emoji
                          ? 'from-blue-400/20 to-transparent'
                          : 'from-white/5 to-transparent opacity-0 group-hover/emoji:opacity-100')
                        : 'hidden'
                      }`}></div>

                      {/* Glass Highlight (nur Dark) */}
                      <div className={`absolute top-1 left-1 w-2 h-2 bg-white/30 rounded-full blur-sm opacity-0 group-hover/emoji:opacity-100 transition-opacity duration-200 ${isDarkMode ? '' : 'hidden'}`}></div>

                      <span className="relative text-xl z-10">{emoji}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}

            {/* Empty State */}
            {Object.keys(filteredCategories).length === 0 && (
              <div className="relative group/empty">
                <div className={`absolute inset-0 ${isDarkMode
                  ? 'bg-gradient-to-br from-gray-900/20 via-gray-800/15 to-gray-900/20 border border-white/20 backdrop-blur-xl shadow-[0_4px_16px_rgba(0,0,0,0.2)]'
                  : 'bg-slate-50 border border-slate-200'
                } rounded-2xl`}></div>
                <div className={`absolute inset-0 ${isDarkMode
                  ? 'bg-gradient-to-t from-white/5 via-transparent to-white/10'
                  : ''
                } rounded-2xl`}></div>

                <div className="relative text-center py-8 px-6">
                  <div className="text-4xl mb-4">🔍</div>
                  <p className={`${isDarkMode ? 'text-white' : 'text-slate-900'} font-medium`}>Keine Emojis gefunden für "{searchTerm}"</p>
                  <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-slate-600'} mt-2`}>Versuchen Sie andere Suchbegriffe wie "office", "shop" oder "tool"</p>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-6">
            {/* Divider */}
            <div className="mb-6 relative">
              <div className={`h-px bg-gradient-to-r from-transparent ${isDarkMode ? 'via-white/40' : 'via-slate-200'} to-transparent`}></div>
              <div className={`absolute top-0 left-1/2 transform -translate-x-1/2 w-16 h-px bg-gradient-to-r from-blue-400/60 to-blue-400/60 blur-sm ${isDarkMode ? '' : 'hidden'}`}></div>
            </div>

            <div className="flex justify-between items-center">
              <p className={`text-xs ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>
                Wählen Sie ein Emoji durch Klicken aus
              </p>

              {/* Fertig Button */}
              <button
                onClick={onClose}
                className={`relative group/done overflow-hidden rounded-2xl transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] ${
                  isDarkMode ? '' : 'bg-brand hover:bg-[#1D4ED8]'
                }`}
                style={isDarkMode ? {
                  background: 'linear-gradient(135deg, rgba(59,130,246,0.7) 0%, rgba(37,99,235,0.7) 50%, rgba(29,78,216,0.7) 100%)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255,255,255,0.3)',
                  boxShadow: '0 8px 32px rgba(59,130,246,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
                } : {}}
              >
                {/* Dynamic Glass Highlights (nur Dark) */}
                <div className={`absolute inset-0 bg-gradient-to-br from-white/0 via-white/5 to-white/0 group-hover/done:from-white/10 group-hover/done:via-white/20 group-hover/done:to-white/10 transition-all duration-500 ${isDarkMode ? '' : 'hidden'}`}></div>

                <div className={`relative z-10 px-6 py-3 font-medium text-white`}>
                  Fertig
                </div>

                {/* Hover Wave (nur Dark) */}
                <div className={`absolute inset-0 opacity-0 group-hover/done:opacity-100 transition-opacity duration-500 ${isDarkMode ? '' : 'hidden'}`}>
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 -translate-x-full group-hover/done:translate-x-full transition-transform duration-1000"></div>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmojiPicker;
